import type {} from '@redux-devtools/extension';

type DevtoolsConnection = ReturnType<NonNullable<Window['__REDUX_DEVTOOLS_EXTENSION__']>['connect']>;

export interface ReducerDevtoolsOptions {
    /** Instance name shown in the DevTools dropdown (e.g. "BasketTrading [abc123]") */
    name: string;
    /** Maximum number of actions to keep in history. @default 50 */
    maxAge?: number;
}

export interface ReducerDevtoolsConnection<S, A extends { type: string }> extends Disposable {
    /** Send an action and the resulting state to DevTools */
    send(action: A, state: S): void;
    /** Initialize DevTools with the current state snapshot */
    init(state: S): void;
    /** Disconnect and reconnect with a new instance name */
    setName(name: string): void;
}

/**
 * Connect a reducer to Redux DevTools for read-only action/state inspection.
 *
 * Returns `undefined` when the extension is not available (production, no browser extension),
 * so callers can short-circuit with optional chaining: `connection?.send(action, state)`.
 *
 * The returned connection implements `Disposable` for use with `disposableStack.use()`.
 */
export function connectReducerDevtools<S, A extends { type: string }>(
    options: ReducerDevtoolsOptions,
): ReducerDevtoolsConnection<S, A> | undefined {
    let extensionConnector: Window['__REDUX_DEVTOOLS_EXTENSION__'] | false;
    try {
        extensionConnector = window.__REDUX_DEVTOOLS_EXTENSION__;
    } catch {
        // window may not exist (SSR, tests without jsdom)
    }

    if (!extensionConnector) {
        return undefined;
    }

    let connection: DevtoolsConnection = extensionConnector.connect({
        name: options.name,
        maxAge: options.maxAge,
    });

    let lastState: S | undefined;

    const disconnectCurrent = () => {
        if (typeof (connection as unknown as { unsubscribe?: () => void }).unsubscribe === 'function') {
            (connection as unknown as { unsubscribe: () => void }).unsubscribe();
        }
    };

    return {
        send(action: A, state: S) {
            lastState = state;
            connection.send(action, state);
        },
        init(state: S) {
            lastState = state;
            connection.init(state);
        },
        setName(name: string) {
            disconnectCurrent();
            connection = extensionConnector!.connect({
                name,
                maxAge: options.maxAge,
            });
            if (lastState !== undefined) {
                connection.init(lastState);
            }
        },
        [Symbol.dispose]() {
            disconnectCurrent();
        },
    };
}
